import { useNavigate, useParams } from "react-router-dom";
import ButtonComponent from "../../../../components/button/button";
import { useState, useEffect } from "react";
import InputComponent from "../../../../components/input/input";
import { getMe, getUserById, updateUser } from "../../../../features/search/service/user-service";
import { getNonSuperAdminRoles, getRoles, getRoleById } from "../../../../features/search/service/role-service";
import { getCompaniesListSelect } from "../../../../features/search/service/company-service";
import { SelectOption } from "../../../../shared/types/select-options";
import { User } from "../../../../shared/types/user";
import { Role } from "../../../../shared/types/role";
import SelectComponent from "../../../../components/input/select";
import { MenuTop } from '../../../../components/menu/top/menu-top';
import './styles/edit.css';
import ButtonSecondaryComponent from "../../../../components/button-secondary/button";
import { ROLES } from "../../../../shared/consts/roles";
import { useTranslation } from "../../../../shared/i18n/i18n-context";

export const AdminUsersEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('admin');
    
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');
    const [me, setMe] = useState<User | null>(null);
    const [meRole, setMeRole] = useState<Role | null>(null);
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchingUser, setFetchingUser] = useState<boolean>(true);

    // Buscar dados do usuário logado
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await getMe();
                setMe(userData);

                // Buscar a role do usuário logado
                if (userData.role_id) {
                    const roleData = await getRoleById(userData.role_id);
                    setMeRole(roleData);
                }
            } catch (error) {
                console.error('❌ Erro ao buscar dados do usuário:', error);
            }
        };
        fetchUser();
    }, []);

    // Buscar dados do usuário a ser editado
    useEffect(() => {
        const fetchUserToEdit = async () => {
            if (!id) {
                navigate('/admin-painel');
                return;
            }

            try {
                setFetchingUser(true);
                const userData = await getUserById(id);
                
                if (!userData) {
                    console.error('❌ Usuário não encontrado');
                    navigate('/admin-painel');
                    return;
                }

                setName(userData.name || '');
                setEmail(userData.email || '');
                setRoleId(userData.role_id ? String(userData.role_id) : '');
                setCompanyId(userData.companyId ? String(userData.companyId) : '');
            } catch (error) {
                console.error('❌ Erro ao buscar usuário para editar:', error);
                navigate('/admin-painel');
            } finally {
                setFetchingUser(false);
            }
        };

        fetchUserToEdit();
    }, [id, navigate]);

    // Buscar roles e empresas disponíveis
    useEffect(() => {
        const fetchRolesAndCompanies = async () => {
            if (!me || !meRole) {
                return;
            }

            try {
                let rolesData: SelectOption[] = [];
                let companiesData: SelectOption[] = [];

                const meRoleDescription = meRole.description;

                if (meRoleDescription === ROLES.SUPER_ADMIN) {
                    [rolesData, companiesData] = await Promise.all([
                        getRoles(),
                        getCompaniesListSelect()
                    ]);
                } else if (meRoleDescription === ROLES.ADMIN) {
                    [rolesData, companiesData] = await Promise.all([
                        getNonSuperAdminRoles(),
                        getCompaniesListSelect()
                    ]);
                }
                
                setRoles(rolesData);
                setCompanies(companiesData);

                // Se roleId ainda não foi definido, define o primeiro
                if (!roleId && rolesData.length > 0) {
                    setRoleId(String(rolesData[0].id));
                }

                if (!companyId && companiesData.length > 0) {
                    setCompanyId(String(companiesData[0].id));
                }

            } catch (error) {
                console.error('❌ Erro ao buscar roles/empresas:', error);
            }
        };

        fetchRolesAndCompanies();
    }, [companyId, me, meRole, roleId]);

    const handleUpdateUser = async () => {
        if (!id) {
            console.error('❌ ID do usuário não fornecido');
            return;
        }

        if (!name || name.trim() === '') {
            alert(t('nameRequired'));
            return;
        }

        if (!email || email.trim() === '') {
            alert(t('emailRequired'));
            return;
        }

        if (!roleId || roleId === '' || roleId === 'undefined') {
            alert(t('roleRequired'));
            return;
        }

        if (!me?.companyId && !companyId) {
            alert(t('companyRequired'));
            return;
        }

        const payload: any = {
            name: name.trim(),
            email: email.trim(),
            roleId: roleId,
            companyId: companyId || me?.companyId,
        };

        setLoading(true);

        try {
            const updatedUser = await updateUser(id, payload);
            
            if (updatedUser) {
                navigate('/admin-painel/users');
            }
            
        } catch (error) {
            console.error('❌ Erro ao atualizar usuário:', error);
            alert(t('updateUserError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin-painel/users');
    };

    if (fetchingUser) {
        return (
            <div>
                <MenuTop />
                <div className="edit-user-container">
                    <p style={{ color: '#666', fontSize: '16px' }}>
                        {t('loadingUserData')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <MenuTop />
            <div className="edit-user-container">
                <h1>{t('editUser')}</h1>

                <InputComponent
                    id="id-input-name"
                    label={t('name')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-email"
                    label={t('email')}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />

                {meRole?.description === ROLES.SUPER_ADMIN && companies.length > 0 && (
                    <SelectComponent
                        label={t('company')}
                        value={companyId || ''}
                        onChange={(value) => {
                            setCompanyId(String(value));
                        }}
                        options={companies}
                        styles={{ marginTop: '10px' }}
                    />
                )}

                {roles.length > 0 ? (
                    <SelectComponent
                        label={t('role')}
                        value={roleId || ''}
                        onChange={(value) => {
                            setRoleId(String(value));
                        }}
                        options={roles}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                        {t('loadingRoles')}
                    </p>
                )}

                <div className="button-container">
                    <ButtonSecondaryComponent
                        label={t('cancel')}
                        onClick={handleCancel}
                        disabled={loading}
                    />
                    <ButtonComponent
                        label={loading ? t('saving') : t('saveChanges')}
                        onClick={handleUpdateUser}
                        disabled={loading || !roleId || roleId === '' || roleId === 'undefined'}
                    />
                </div>
            </div>
        </div>
    );
};
